import { quantitative, section, number, equation, table, example } from '../batch2/shared.ts';
export const batch4Reason = [
  quantitative(
    'reason-deadlines',
    'reason',
    'Five requests',
    [
      section('schedule', 'Separate a feasible schedule from an optimal one', [
        'A scheduling problem needs a precise resource model. Here one worker processes one task at a time, all tasks are available at the start, and a task cannot be interrupted. Its completion time is the sum of the durations of tasks placed before it plus its own duration. A deadline constrains completion, not the moment when work begins. Finishing exactly at a deadline counts as on time.',
        'To show that k tasks can be finished on time, exhibit an ordered list and check every cumulative completion time. To show that k is the maximum, also rule out a larger number. These are different obligations: a schedule is a construction, while a capacity argument or exhaustive comparison supplies a bound. Choosing the earliest deadline first is useful for checking a chosen subset, but deciding which tasks to keep is another step.',
        example(
          'A smaller selection',
          'Three requests take 3, 2 and 2 hours and have deadlines 3, 4 and 5 hours. Doing the three-hour request first leaves room for at most one two-hour request by hour 5. Alternatively the two short requests finish at hours 2 and 4. Either way two requests can be on time.',
          table(
            'One feasible pair',
            ['Task duration', 'Completion', 'Deadline'],
            [
              ['2 hours', '2', '4'],
              ['2 hours', '4', '5'],
            ],
          ),
          'All three tasks require 7 hours, while their latest deadline is hour 5. That total-time bound proves that two is optimal, regardless of their order.',
        ),
        'For a selected group of tasks, an order with nondecreasing deadlines is sufficient whenever any feasible order exists. To see why, consider adjacent tasks whose deadlines are reversed. Swap them. The earlier-deadline task now finishes earlier. The other finishes at the old pair’s ending time, which was no later than the tighter deadline and is therefore no later than its own. Repeating these swaps removes every deadline inversion without losing feasibility.',
      ]),
      section(
        'apply',
        'Check the cumulative times and the upper bound',
        [
          'For the daily requests, omit A and schedule B, C, D, E. The cumulative completion times are 2, 5, 6 and 9. Each is no later than its respective deadline. C and D have the same deadline, so either order between those two also works in this particular schedule.',
          table(
            'A four-request schedule',
            ['Request', 'Duration', 'Finish', 'Deadline'],
            [
              ['B', '2', '2', '5'],
              ['C', '3', '5', '7'],
              ['D', '1', '6', '7'],
              ['E', '3', '9', '9'],
            ],
          ),
          'All five requests together require 4 + 2 + 3 + 1 + 3 = 13 hours. Every deadline is at or before hour 9. A single worker starting at zero cannot supply 13 hours of work by hour 9, so five on-time requests are impossible. The construction and bound meet at four.',
          'Taking A just because its deadline comes first uses four early hours and may reduce the number that fit. Earliest-deadline ordering is a feasibility tool after selection, not a rule that every early-deadline task must be accepted. If tasks could overlap on different workers, or arrived after time zero, this one-worker calculation would no longer settle the problem.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-deadlines',
      'One worker starts at time 0 and can do one request at a time, without interruption. All five are available immediately. Durations and completion deadlines, in hours, are A: (4, 4), B: (2, 5), C: (3, 7), D: (1, 7), E: (3, 9). Requests may be omitted and performed in any order. What is the maximum number that can finish on or before their deadlines?',
      4,
      'requests',
      'B, C, D, E finish at 2, 5, 6, 9; all five require 13 hours before a latest deadline of 9.',
      ['schedule', 'apply'],
      0,
    ),
  ),
  quantitative(
    'reason-envelopes',
    'reason',
    'Four envelopes',
    [
      section('cycles', 'Describe where each object goes', [
        'A one-to-one assignment of letters to envelopes is a permutation: every letter has exactly one destination and every envelope receives exactly one letter. Label each letter and its correct envelope with the same symbol. A misplaced assignment has no letter sent to its own symbol. Counting independently available destinations for each letter is dangerous, because using an envelope removes it from later choices.',
        'A useful representation follows the destinations as arrows. Start at one symbol and repeatedly follow the arrow to the envelope bearing the next symbol. Because every symbol has one incoming and one outgoing arrow, the route eventually closes into a cycle. Other symbols form separate cycles. A cycle of length one is a correctly placed letter, so it is forbidden here.',
        example(
          'Three letters',
          'With symbols A, B and C, no one can stay fixed. The only possible cycle length is three: A→B→C→A or A→C→B→A. A two-cycle would leave the third letter in its own envelope.',
          table(
            'Assignments without a fixed letter',
            ['Cycle', 'Letters in envelopes A, B, C'],
            [
              ['A→B→C→A', 'C, A, B'],
              ['A→C→B→A', 'B, C, A'],
            ],
          ),
          'These are two assignments, not six. Starting to describe the same cycle at B instead of A does not change where any letter is placed.',
        ),
        'For four symbols, the allowed cycle sizes must sum to four without using one. The possibilities are therefore one cycle of length four, or two separate cycles of length two. This partitions all valid assignments into disjoint classes. Counting those classes avoids both leaving out a structure and counting it twice.',
      ]),
      section(
        'apply',
        'Count each allowed cycle structure',
        [
          'For one four-cycle, fix A as the starting point of its written description. Arrange B, C and D after A in any of their 3 × 2 × 1 = 6 orders, and return to A. Fixing the start avoids counting the same directed cycle four times. Reversing the direction generally changes the assignment and should still be counted.',
          'For two two-cycles, choose A’s partner in three ways. The remaining two symbols must be partners with each other. These possibilities are AB with CD, AC with BD, and AD with BC. There are three, not six, because exchanging the written order of the two pairs changes no assignment.',
          equation(
            'Disjoint structures',
            'valid assignments',
            '6 + 3 = 9',
            'A four-cycle and two two-cycles cannot describe the same permutation.',
          ),
          'The proposed count can be checked by listing all 4! = 24 permutations and rejecting those with any fixed position. The tempting product 3⁴ allows two letters to choose the same envelope and therefore counts objects outside the problem. If some letters could stay correctly placed, the cycle partition would need additional structures containing one-cycles.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-envelopes',
      'Four distinct letters A, B, C and D must be put into four envelopes labeled A, B, C and D, one letter per envelope. How many assignments put every letter into an incorrectly labeled envelope? Count complete assignments, not the order in which you insert letters.',
      9,
      'assignments',
      'Six four-cycles and three pairs of two-cycles give nine assignments.',
      ['cycles', 'apply'],
      0,
    ),
  ),
  quantitative(
    'reason-jugs',
    'reason',
    'Three containers',
    [
      section('states', 'Record what determines the next legal move', [
        'A state describes the information needed to decide what can happen next. For fixed-capacity containers, the amounts currently inside each container determine every possible pour. If there is no tap or drain, total water stays constant. Two different histories that reach the same three amounts have the same future options, so a shortest-route search need not revisit that state through a longer history.',
        'A move pours from one chosen container to another until either the source is empty or the destination is full. The transferred amount is the smaller of the source amount and the destination’s unused capacity. Stopping at an arbitrary unmarked amount would be a different rule and could create shortcuts that are illegal here.',
        equation(
          'A legal pour',
          'amount transferred',
          'min(source amount, destination capacity − destination amount)',
          'Subtract this from the source and add it to the destination.',
        ),
        example(
          'A smaller state',
          'With capacities 4 and 3 liters, the state (4,0) can pour from the first container to the second. The destination has room for 3, so the new state is (1,3). Pouring back immediately restores (4,0); it does not create a new useful possibility.',
          table(
            'Explore by number of moves',
            ['Layer', 'Meaning'],
            [
              ['0', 'The starting state'],
              ['1', 'All states from one legal pour'],
              ['2', 'Previously unseen states reached with one more pour'],
            ],
          ),
          'Exploring all states in one layer before the next is breadth-first search. The first layer containing a goal proves its minimum move count, because all shorter routes have already been exhausted.',
        ),
        'For integral starting amounts and integral capacities, every legal transfer here remains integral. The search is consequently finite: each amount lies between zero and its capacity, and their sum is fixed. This finiteness makes an exhaustive shortest-route certificate practical, rather than relying on a guessed pouring sequence.',
      ]),
      section(
        'apply',
        'A route and an independent minimum check',
        [
          'Track the containers in the order 8-liter, 5-liter, 3-liter. One route reaches the required state in seven pours. Each row below follows the full-or-empty stopping rule.',
          table(
            'Seven pours',
            ['Pour', 'Amounts afterward'],
            [
              ['Start', '(8, 0, 0)'],
              ['8L → 5L', '(3, 5, 0)'],
              ['5L → 3L', '(3, 2, 3)'],
              ['3L → 8L', '(6, 2, 0)'],
              ['5L → 3L', '(6, 0, 2)'],
              ['8L → 5L', '(1, 5, 2)'],
              ['5L → 3L', '(1, 4, 3)'],
              ['3L → 8L', '(4, 4, 0)'],
            ],
          ),
          'The sequence proves that seven is achievable. To establish minimality, enumerate all ordered source/destination pairs from every state at distances zero through six, discard transfers of zero and previously visited states, and check the resulting layer. None contains (4,4,0); it first appears at distance seven. The table lists every newly reached state at each distance through six, so each shorter possibility can be checked against the goal.',
          table(
            'All states before the seventh pour',
            ['Distance', 'Newly reached states'],
            [
              ['0', '(8,0,0)'],
              ['1', '(3,5,0); (5,0,3)'],
              ['2', '(0,5,3); (3,2,3); (5,3,0)'],
              ['3', '(6,2,0); (2,3,3)'],
              ['4', '(6,0,2); (2,5,1)'],
              ['5', '(1,5,2); (7,0,1)'],
              ['6', '(1,4,3); (7,1,0)'],
            ],
          ),
          'One container holding four liters is not yet the stated goal: the target requires four in each of the two larger containers and none in the smallest. Likewise, a strategy that discards water or refills from a tap solves a different puzzle. Keeping both the state and the goal explicit prevents these seemingly convenient shortcuts.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-jugs',
      'You have unmarked containers of capacities 8, 5 and 3 liters, initially holding 8, 0 and 0 liters. A move pours between two containers until the source is empty or the destination is full. No water may be added or discarded. What is the minimum number of pours needed to reach amounts 4, 4 and 0 liters, respectively?',
      7,
      'pours',
      'A seven-pour route exists; exhaustive state layers contain no goal within six pours.',
      ['states', 'apply'],
      0,
    ),
  ),
  quantitative(
    'reason-dominoes',
    'reason',
    'A narrow board',
    [
      section('recurrence', 'Partition arrangements by their first forced choice', [
        'A recurrence counts a larger object by relating it to smaller objects of the same kind. Its justification comes before its arithmetic. Divide all arrangements into cases that cannot overlap and that cover every possibility. Then show exactly how an arrangement in each case corresponds to a smaller complete problem. If either direction of that correspondence fails, the proposed recurrence may overcount or omit possibilities.',
        'For a board two squares high, examine its leftmost uncovered square. A vertical domino covers the entire first column and leaves a shorter two-row board. A horizontal domino covers the top square of each of the first two columns. The bottom-left square then cannot be covered vertically, so a second horizontal domino must go directly below it. That pair leaves a board two columns shorter.',
        {
          kind: 'diagram',
          drawing: 'domino-cases',
          caption: 'The first covered square determines one of two smaller-board cases.',
          description:
            'At the left edge of a two-row board, one vertical domino leaves n minus one columns. A top horizontal domino forces a second horizontal domino below it, leaving n minus two columns.',
        },
        equation(
          'Count by the left edge',
          'T(n)',
          'T(n − 1) + T(n − 2)',
          'T(n) counts tilings of a two-row, n-column board; use the relation for n ≥ 2.',
        ),
        'There is one tiling of a zero-column board: choose no pieces. This is a counting convention that makes the recurrence work at the boundary, not a claim that an empty board physically contains a domino. There is also one tiling of a one-column board, using one vertical piece.',
        example(
          'A three-column board',
          'A 2×2 board has two tilings: two vertical pieces or two horizontal ones. A 2×3 board therefore has T(2) + T(1) = 2 + 1 = 3 tilings. Two start with a vertical piece; one starts with a horizontal pair.',
          table(
            'Small boards',
            ['Columns', 'Tilings'],
            [
              ['0', '1'],
              ['1', '1'],
              ['2', '2'],
              ['3', '3'],
            ],
          ),
          'The cases are disjoint because the top-left square cannot be covered both horizontally and vertically in the same tiling. Every tiling belongs to one case, so adding the counts is valid.',
        ),
      ]),
      section(
        'apply',
        'Continue the justified recurrence',
        [
          'Using the same two left-edge cases gives T(4)=5, T(5)=8, T(6)=13 and T(7)=21. These are counts of finished coverings, independent of which order the pieces were physically placed.',
          table(
            'Daily continuation',
            ['Columns', 'Calculation', 'Tilings'],
            [
              ['4', '3 + 2', '5'],
              ['5', '5 + 3', '8'],
              ['6', '8 + 5', '13'],
              ['7', '13 + 8', '21'],
            ],
          ),
          'The seven pieces are indistinguishable. Labeling them and multiplying by 7! would count insertion labels that the prompt does not distinguish. Mirror images count separately only when the occupied pairs of squares differ; the board has fixed positions. A separate exact check can fill the first unoccupied square with each legally oriented domino and recurse until all fourteen squares are covered.',
          'The recurrence depends on the board being exactly two rows high and having no missing squares. With three rows or a hole, the left-edge choice can leave a jagged boundary rather than a smaller rectangle of the same kind. The necessary state would then have to record that boundary instead of only the number of columns.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-dominoes',
      'A fixed rectangular board has 2 rows and 7 columns of unit squares. Cover it with seven indistinguishable 1×2 dominoes, allowing rotation but no overlaps or gaps. Two coverings differ if some domino occupies a different pair of squares. How many coverings are there?',
      21,
      'coverings',
      'Classifying the left edge gives T(n)=T(n−1)+T(n−2), with T(0)=T(1)=1; T(7)=21.',
      ['recurrence', 'apply'],
      0,
    ),
  ),
  quantitative(
    'reason-route-pass',
    'reason',
    'One free road',
    [
      section('resource', 'A remaining privilege is part of the state', [
        'A shortest-route problem with a one-use pass is not determined only by the current location. Arriving at a junction cheaply after spending the pass can be worse than arriving slightly more expensively with the pass still available. These are different states because they allow different future costs. A general search can track pairs such as (junction, pass unused) and (junction, pass used).',
        'For a small network with only a few complete routes, there is an even simpler approach: list those routes. For each one, find its best possible use of the pass. If the pass makes one entire road free and all tolls are nonnegative, using it on that route’s largest toll minimizes that route’s paid total. Then compare the optimized totals across routes.',
        equation(
          'Best cost on a fixed route',
          'paid total',
          'sum of tolls − largest toll',
          'The pass removes one whole toll, not a fixed amount from every road.',
        ),
        example(
          'Why the ordinary shortest route can lose',
          'Suppose one route has tolls 5 and 5 while another has tolls 1 and 12. Without a pass, the first costs 10 and the second 13. With one free road, their best costs become 5 and 1. The initially more expensive route becomes cheaper.',
          table(
            'Compare like with like',
            ['Route', 'No pass', 'Best with pass'],
            [
              ['5, 5', '10', '5'],
              ['1, 12', '13', '1'],
            ],
          ),
          'Optimizing the no-pass total and only then spending the pass need not find the overall optimum. The operation changes which route is best.',
        ),
        'For a large network, enumerating routes can be impractical. The two-state representation instead allows every ordinary road to lead from unused to unused at its toll, from used to used at its toll, or from unused to used at zero. There is no transition that restores the pass. That makes the resource constraint explicit.',
      ]),
      section(
        'apply',
        'Compare all four complete routes',
        [
          'The daily directed network moves from A to either B or C, then D or E, then F. Those choices produce exactly four routes. There are no other roads and no cycles, so this list is exhaustive.',
          table(
            'All route costs after the best waiver',
            ['Route', 'Tolls', 'Best paid total'],
            [
              ['A–B–D–F', '8, 2, 8', '10'],
              ['A–B–E–F', '8, 5, 4', '9'],
              ['A–C–D–F', '3, 7, 8', '10'],
              ['A–C–E–F', '3, 6, 4', '7'],
            ],
          ),
          'The minimum is seven, achieved on A–C–E–F by waiving the toll of six on C→E. The tolls still paid are three and four. The full route list proves that no legal alternative does better, not merely that this route works.',
          'Choosing the globally most expensive road to waive without considering the route needed to reach it would be another error. A free road only helps if it lies on a useful complete route. If the pass covered at most a fixed monetary amount rather than an entire toll, each route’s discount would be capped and the comparison would change.',
        ],
        'Daily application',
      ),
    ],
    number(
      'b4-pass',
      'A directed road network has only these roads and tolls: A→B: 8; A→C: 3; B→D: 2; B→E: 5; C→D: 7; C→E: 6; D→F: 8; E→F: 4. You travel from A to F and may waive the entire toll on one road of your route. What is the minimum total toll you must pay?',
      7,
      'toll units',
      'Enumerating the four routes after their best single waiver gives 10, 9, 10 and 7.',
      ['resource', 'apply'],
      0,
    ),
  ),
];
